import React, {Component, Fragment} from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {Waypoint} from '@homeaway/react-unveil';
import {AspectRatioCard} from '@homeaway/react-aspect-ratio-card';
import determineIfMobile from '@homeaway/react-determine-if-mobile';
import {CardCarousel} from '@homeaway/react-card-carousel';
import {createGetBathrooms, createGetBedrooms, createGetSleeps} from '../../details/components/with-tripboard-query';
import ListingMiniHit, {ASPECT_RATIO_4X3} from '../../listing/listing-mini-hit';
import recommendedPropertyActions from '../actions';
import {carouselNext, carouselPrev, propertyViewed, propertiesRecommended, recommendedListingClick} from '../edap';
import TripboardButtonContainer from '../../../components/Hit/HitSubComponents/TripboardButtonContainer/TripboardButtonContainer';
import {injectIntl} from 'react-intl';
import './recommended-properties-carousel.less';

const breakpoints = {
    '2340': 8,
    '2040': 7,
    '1740': 6,
    '1440': 5,
    '1140': 4,
    '840': 3,
    '769': 2,
    '680': 3,
    '490': 3,
    '0': 2
};

const getAmenities = (cardProps, formatMessage) => {
    const getBathrooms = createGetBathrooms(formatMessage);
    const getBedrooms = createGetBedrooms(formatMessage);
    const getSleeps = createGetSleeps(formatMessage);
    return {
        bathrooms: getBathrooms(cardProps),
        bedrooms: getBedrooms(cardProps),
        sleeps: getSleeps(cardProps)
    };
};

const placeHolderImage =
    'https://csvcus.homeaway.com/rsrcs/cdn-logos/2.6.1/bce/brand/misc/property-image-placeholder.svg';

const getImage = (cardProps) => {
    return cardProps.images && cardProps.images[0] && cardProps.images[0].c6_uri
        ? cardProps.images[0].c6_uri
        : placeHolderImage;
};

const getControlOffset = () => {
    return (width, controlHeight) => {
        return AspectRatioCard.getControlOffset(width, controlHeight, ASPECT_RATIO_4X3);
    };
};

export const RecommendedPropertyCarousel = (props) => {
    const {
        recommendations,
        cardIndex = 0,
        isMobile,
        title,
        handleNextArrowClick,
        handlePrevArrowClick,
        handleResize,
        handleHitClick,
        intl: {formatMessage},
        stay
    } = props;

    if (!formatMessage || recommendations.length < 1) {
        return null;
    }

    return (
        <div className="recommended-properties-carousel">
            <CardCarousel
                breakpoints={breakpoints}
                cardIndex={cardIndex}
                onNextClick={handleNextArrowClick}
                onPreviousClick={handlePrevArrowClick}
                onResize={handleResize}
                title={title}
                paging
                scrolling={isMobile}
                scrollingPeekPct={isMobile ? 0.5 : 0.2}
                getControlOffset={getControlOffset()}
            >
                {recommendations.map((listing, index) => {
                    const amenities = getAmenities(listing, formatMessage);
                    const imageUrl = getImage(listing);
                    return (
                        <Fragment key={listing.listingId}>
                            <TripboardButtonContainer
                                headline={listing.headline}
                                listingId={listing.listingId}
                                rating={listing.averageRating}
                                reviewCount={listing.reviewCount}
                                thumbnailUrl={imageUrl}
                                adultsCount={stay && stay.adultsCount}
                                childrenCount={stay && stay.childrenCount}
                                petIncluded={stay && stay.petsIncluded}
                                startDate={stay && stay.dateRange && stay.dateRange.arrival}
                                endDate={stay && stay.dateRange && stay.dateRange.departure}
                            />
                            <ListingMiniHit
                                {...listing}
                                {...amenities}
                                aspectRatio={ASPECT_RATIO_4X3}
                                imageUri={imageUrl}
                                onClick={handleHitClick(listing.listingId, index)}
                                stay={!!listing.priceSummary}
                            />
                        </Fragment>
                    );
                })}
            </CardCarousel>
        </div>
    );
};

export class RecommendedPropertyCarouselWrapper extends Component {
    constructor(props) {
        super(props);
        this.handleNextArrowClick = this.handleNextArrowClick.bind(this);
        this.handlePrevArrowClick = this.handlePrevArrowClick.bind(this);
        this.handleHitClick = this.handleHitClick.bind(this);
        this.onEnter = this.onEnter.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.lastCardSeen = 0;
    }

    componentDidMount() {
        const {boardId, recommendations} = this.props;
        propertiesRecommended(boardId, (recommendations && recommendations.length) || 0);
    }

    componentDidUpdate(prevProps) {
        const {isInView, numCardsVisible, cardIndex} = this.props;
        const {isInView: prevIsInView, numCardsVisible: prevNumCardsVisible, cardIndex: prevCardIndex} = prevProps;
        if (isInView && (!prevIsInView || numCardsVisible > prevNumCardsVisible || cardIndex > prevCardIndex)) {
            this.firePropertiesViewed();
        }
    }

    firePropertiesViewed() {
        const {boardId, recommendations, numCardsVisible, cardIndex} = this.props;
        const total = recommendations.length;
        const finalIndex = Math.min(total, cardIndex + numCardsVisible);
        const firstIndex = this.lastCardSeen;
        this.lastCardSeen = finalIndex;

        for (let index = firstIndex; index < finalIndex; index++) {
            const listing = recommendations[index];
            if (listing) {
                propertyViewed(boardId, listing.listingId, index, total);
            }
        }
    }

    onEnter() {
        const {scrolledIntoView} = this.props;
        scrolledIntoView();
    }

    handleResize({numberCardsVisible}) {
        const {changeNumCardsVisible, numCardsVisible} = this.props;

        if (numberCardsVisible > numCardsVisible) {
            changeNumCardsVisible(numberCardsVisible);
        }
    }

    handleNextArrowClick(_, cardIndex) {
        const {changeCardIndex, boardId} = this.props;
        changeCardIndex(cardIndex);
        carouselNext(boardId);
    }

    handlePrevArrowClick(_, cardIndex) {
        const {changeCardIndex, boardId} = this.props;
        changeCardIndex(cardIndex);
        carouselPrev(boardId);
    }

    handleHitClick(listingId, position) {
        const {boardId, recommendations} = this.props;
        const total = recommendations.length;
        return () => {
            recommendedListingClick(boardId, listingId, position, total);
        };
    }

    render() {
        const {formatMessage} = this.props.intl;
        const carouselProps = {
            ...this.props,
            title: formatMessage({id: 'tripboards.recommendedProperties.recommendationMessage'}),
            handleHitClick: this.handleHitClick,
            handleNextArrowClick: this.handleNextArrowClick,
            handlePrevArrowClick: this.handlePrevArrowClick,
            handleResize: this.handleResize
        };
        return (
            <Waypoint onEnter={this.onEnter}>
                <RecommendedPropertyCarousel {...carouselProps} />
            </Waypoint>
        );
    }
}
const mapStateToProps = (
    {
        system: {isMobile},
        tripboard: {recommendations: {cardIndex = 0, numCardsVisible = 0, isInView = false} = {}} = {}
    },
    {recommendations}
) => ({
    cardIndex: Math.min(cardIndex, Math.max(0, recommendations.length - 1)),
    numCardsVisible,
    isInView,
    isMobile
});

const mapDispatchToProps = {
    changeCardIndex: recommendedPropertyActions.changeCardIndex,
    changeNumCardsVisible: recommendedPropertyActions.changeNumCardsVisible,
    scrolledIntoView: recommendedPropertyActions.scrolledIntoView
};

export default compose(connect(mapStateToProps, mapDispatchToProps), injectIntl, determineIfMobile)(
    RecommendedPropertyCarouselWrapper
);



// WEBPACK FOOTER //
// ./src/universal/tripboard/recommended-properties/components/recommended-properties-carousel.js